import { ChangeDetectionStrategy, Component, inject, ProviderToken } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { SidenavComponent, SidenavItem } from '@examdojo/shell';
import { map } from 'rxjs';
import { RootUrlParts } from '../app.model';
import { OpenCourseFormDialogEffectService } from '../effects/open-course-form-dialog-effect.service';
import { FEATURE_TO_ICON, FEATURE_TO_LABEL_KEY, Features } from '../features/features.model';

interface FeaturePageConfig {
  feature: Features;
  flag: ExamDojoFeatureFlag | null;
}

@Component({
  selector: 'dojo-shell',
  imports: [RouterOutlet, SidenavComponent],
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full safe-area-top safe-area-bottom' },
  providers: [OpenCourseFormDialogEffectService],
})
export class ShellComponent {
  constructor(private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>) {}

  private readonly instantiatedServices = [OpenCourseFormDialogEffectService].map((service: ProviderToken<unknown>) =>
    inject(service),
  );

  private readonly featurePages: FeaturePageConfig[] = [
    { feature: Features.TopicPractice, flag: null },
    { feature: Features.LearnHub, flag: null },
    { feature: Features.Assessments, flag: null },
    { feature: Features.Exams, flag: ExamDojoFeatureFlag.Exams },
    { feature: Features.QuestionBank, flag: ExamDojoFeatureFlag.QuestionBank },
  ];

  readonly sidenavItems$ = this.featureFlagService.selectAll().pipe(
    map((allFlags) =>
      this.featurePages
        .filter(({ flag }) => !flag || allFlags[flag])
        .map(
          ({ feature }): SidenavItem => ({
            route: feature,
            labelKey: FEATURE_TO_LABEL_KEY[feature],
            icon: FEATURE_TO_ICON[feature],
          }),
        ),
    ),
  );

  readonly state = connectState({
    sidenavItems: this.sidenavItems$,
  });

  readonly settingsRoute = RootUrlParts.Settings;
}
